import { css } from '@emotion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { MouseEvent, useCallback, useState } from 'react';
import { ShowChatBotAtom } from '../../../components/ChatBotSection/chatbot-state';
import {
  ChatConversationIdAtom,
  IsReceivingMessagesAtom,
  ShowChatMenuAtom,
  useConversations,
} from '../hooks/chatActionHooks';
import { ChatMenuContainer } from './ChatMenu';

const HeaderStyles = css`
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  align-items: center;
  column-gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--mui-palette-divider);
  background: var(--mui-palette-background-paper);
`;

/* Same mark as the app-header assistant button: white sparkle on the primary
 * purple circle. */
const IconBadgeStyles = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--mui-palette-primary-main);
  color: var(--mui-palette-primary-contrastText);
  font-size: 1.125rem;
`;

const TitleBlockStyles = css`
  display: grid;
  min-width: 0;
`;

export const AssistantTitle = 'Foresight Assistant';

/** Subtitle under the panel title: the current conversation's server-assigned
 *  name. Untitled conversations only carry a timestamp fallback, so they (and
 *  brand-new chats) show no subtitle. */
function useConversationSubtitle(): string | undefined {
  const conversationId = useAtomValue(ChatConversationIdAtom);
  const { data: conversations } = useConversations();
  if (conversationId === undefined) {
    return undefined;
  }
  return conversations?.find((conversation) => conversation.id === conversationId)?.title || undefined;
}

export function ChatHeader() {
  const [showChatBot, setShowChatBot] = useAtom(ShowChatBotAtom);
  const [showChatMenu, setShowChatMenu] = useAtom(ShowChatMenuAtom);
  const receivingMessages = useAtomValue(IsReceivingMessagesAtom);
  const [anchor, setAnchor] = useState<HTMLButtonElement | undefined>(undefined);
  const subtitle = useConversationSubtitle();

  const onMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchor(event.currentTarget);
      setShowChatMenu((prev) => !prev);
    },
    [setShowChatMenu]
  );

  return (
    <>
      <Box component='header' css={HeaderStyles}>
        {/* Disabled (not hidden) while a reply streams — switching or deleting
            conversations mid-generation is still unsupported (FD-916). */}
        <IconButton
          onClick={onMenuClick}
          title='toggle menu'
          disabled={receivingMessages}
          aria-haspopup='menu'
          aria-expanded={showChatMenu}
        >
          <MenuIcon color={receivingMessages ? 'disabled' : 'primary'} />
        </IconButton>
        <Box css={IconBadgeStyles} aria-hidden>
          <AutoAwesomeIcon fontSize='inherit' />
        </Box>
        <Box css={TitleBlockStyles}>
          <Typography variant='subtitle2'>{AssistantTitle}</Typography>
          {subtitle && (
            <Typography variant='caption' color='text.secondary' noWrap title={subtitle}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {/* autoFocus: opening the panel unmounts the app-header FAB that was
            focused, which would drop keyboard focus to <body>. The header
            mounts exactly when the panel opens, so focus lands here instead. */}
        {/* Only the side panel has something to close: the atom is false on the
            full-page chat route phones are sent to, so no close button there. */}
        {showChatBot && (
          <IconButton onClick={() => setShowChatBot(false)} title='close assistant' autoFocus>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <ChatMenuContainer open={showChatMenu} onClose={() => setShowChatMenu(false)} anchor={anchor} />
    </>
  );
}
